import React, { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import API from "../../api";
import { getApplicableDiscounts } from "../../api/discounts";
import { listLevels } from "../../api/levels";
import { formatLevelLabel } from "../../utils/formatLevelLabel";
import { listBatches } from "../../api/batches";
import { enrollByStaff, updateEnrollment } from "../../api/enrollments";
import { getEnrollmentPaymentPreview } from "../../api/billingSettings";
import EnrollmentPaymentModal from "./EnrollmentPaymentModal";
import {
    computePeriodEndDate,
    computeRenewStartDate,
    CHARGES_MODE_LABELS,
    formatChargesMode,
    formatPeriodInterval,
    getPeriodIntervalOptions,
    isPeriodBillingMode,
    resolvePeriodCharge,
    resolvePeriodInterval,
    enrollmentChargeSnapshot,
} from "../../utils/periodBillingCopy";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import DatePicker from "../../components/common/DatePicker";
import PageHeaderSection from "../layout/PageHeaderSection";
import PageLoadingShell from "../layout/PageLoadingShell";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { useCurrencyFormatter } from "../../context/RegionalSettingsContext";
import { formatDiscountValue, formatCurrencyRaw } from "../../utils/currency";
import { TextInput, SelectInput } from "../common/form";
import {
    batchPolicyLabel,
    batchPolicySummary,
    studentOverrideShort,
} from "../../utils/carryForwardCopy";
import LockedFieldNote from "../common/LockedFieldNote";
import {
    ENROLLMENT_LOCK_REASONS,
    buildEnrollmentUpdateBody,
    canEditEnrollmentField,
    enrollmentApiBase,
} from "../../utils/enrollmentEditRules";
import {
    parseRegionalDate,
    toISODateFromRegional,
    toRegionalDateFromISO,
    getDateFormatPlaceholder,
} from "../../utils/date";

import { todayIso } from '../../utils/regionalFormat';
import { studentDisplayName } from '../../utils/displayNames';
import { apiErrorMessage } from '../../utils/apiError';

const todayStr = () => todayIso();

const paintBeforeAsync = () =>
    new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

function EnrollmentSavingOverlay({ progress, isEditMode }) {
    const { t } = useTranslation();
    if (typeof document === "undefined") return null;

    const title = isEditMode
        ? t("enrollments.form.saving")
        : progress
            ? t("enrollments.form.enrolling", { current: progress.current, total: progress.total })
            : t("enrollments.form.saving");

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
            aria-live="polite"
            aria-busy="true"
        >
            <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full mx-4 text-center">
                <LoadingSpinner size="lg" />
                <p className="mt-4 font-semibold text-gray-900">{title}</p>
                {!isEditMode && progress?.name && (
                    <p className="mt-1 text-sm text-gray-600">{progress.name}</p>
                )}
                {!isEditMode && progress && progress.total > 0 && (
                    <div className="mt-4 bg-blue-100 rounded-full h-2 overflow-hidden">
                        <div
                            className="bg-blue-600 h-full transition-all duration-300"
                            style={{
                                width: `${Math.max((progress.current / progress.total) * 100, progress.current === 0 ? 8 : 5)}%`,
                            }}
                        />
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

const EnrollmentForm = ({ role }) => {
    const { t } = useTranslation();
    const formatMoney = useCurrencyFormatter();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { id: enrollmentId } = useParams();
    const isEditMode = !!enrollmentId;

    // Master data
    const [students, setStudents] = useState([]);
    const [levels, setLevels] = useState([]);
    const [batches, setBatches] = useState([]);
    const [cascadeLoadError, setCascadeLoadError] = useState("");
    const [discounts, setDiscounts] = useState([]);

    // Form fields
    const [studentId, setStudentId] = useState(""); // For edit mode only
    const [selectedStudentIds, setSelectedStudentIds] = useState([]); // For multi-select in create mode
    const [levelId, setLevelId] = useState("");
    const [batchId, setBatchId] = useState("");
    const [discountId, setDiscountId] = useState("");

    const [enrolledDateISO, setEnrolledDateISO] = useState(todayStr());
    const [enrolledDateUI, setEnrolledDateUI] = useState(toRegionalDateFromISO(todayStr()));

    const [endEnrollmentDateISO, setEndEnrollmentDateISO] = useState("");
    const [endEnrollmentDateUI, setEndEnrollmentDateUI] = useState("");

    const [overrideCarryForward, setOverrideCarryForward] = useState(null);

    const [showOverrideCharges, setShowOverrideCharges] = useState(false);
    const [overrideChargesMode, setOverrideChargesMode] = useState("");
    const [overridePerSessionCharge, setOverridePerSessionCharge] = useState("");
    const [overridePeriodCharge, setOverridePeriodCharge] = useState("");
    const [overridePeriodInterval, setOverridePeriodInterval] = useState("");

    // UI state
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const enrollmentLoadFailedRef = useRef(false);
    const [studentQuery, setStudentQuery] = useState("");
    const [studentOpen, setStudentOpen] = useState(false);
    const [enrollmentProgress, setEnrollmentProgress] = useState(null);
    const [enrollmentResults, setEnrollmentResults] = useState(null);
    const [hasActivity, setHasActivity] = useState(false);
    const [systemManaged, setSystemManaged] = useState(false);
    const [hasCustomSessionRate, setHasCustomSessionRate] = useState(false);
    const [endDateTouched, setEndDateTouched] = useState(false);
    const [renewFromEnrollmentId, setRenewFromEnrollmentId] = useState(null);
    const [paymentModal, setPaymentModal] = useState(null);
    const studentBoxRef = useRef(null);

    const selectedBatch = batches.find(b => (b.batchId ?? b.id) === Number(batchId));
    const batchChargesMode = selectedBatch?.chargesMode ?? selectedBatch?.charges_mode;
    const isPeriodBatch = isPeriodBillingMode(
        overrideChargesMode || batchChargesMode
    );

    const editCtx = { isEdit: isEditMode, systemManaged, hasCustomSessionRate, hasActivity };

    // Helper to get full student name
    const studentFullName = (s) =>
        [s?.firstName, s?.middleName, s?.lastName].filter(Boolean).join(" ").trim();

    const studentLabel = (s) => {
        const name = studentFullName(s) || s?.username || null;
        return name ? `${name} (#${s.__id})` : `#${s.__id}`;
    };

    // STEP 1: Load enrollment data if in edit mode
    useEffect(() => {
        if (!isEditMode || !enrollmentId) return;
        enrollmentLoadFailedRef.current = false;

        let mounted = true;
        (async () => {
            try {
                const res = await API.get(`${enrollmentApiBase(role)}/${enrollmentId}`);
                const enrollment = res?.data;

                if (!mounted || !enrollment) return;

                setHasActivity(!!enrollment.hasActivity);
                setSystemManaged(!!enrollment.systemManaged);
                setHasCustomSessionRate(
                    !!enrollment.hasCustomSessionRate || enrollment.overridePerSessionCharge != null
                );

                // Set form fields
                setStudentId(String(enrollment.studentId || ""));
                setLevelId(String(enrollment.levelId || ""));
                setBatchId(String(enrollment.batchId || ""));

                if (enrollment.enrolledDate) {
                    setEnrolledDateISO(enrollment.enrolledDate);
                    setEnrolledDateUI(toRegionalDateFromISO(enrollment.enrolledDate));
                }

                if (enrollment.endEnrollmentDate) {
                    setEndEnrollmentDateISO(enrollment.endEnrollmentDate);
                    setEndEnrollmentDateUI(toRegionalDateFromISO(enrollment.endEnrollmentDate));
                }

                if (enrollment.discountId) {
                    setDiscountId(String(enrollment.discountId));
                }

                // Set carry forward - handle integer (0/1) or boolean (false/true) from API
                if (enrollment.hasOwnProperty('overrideCarryForward') &&
                    enrollment.overrideCarryForward !== null &&
                    enrollment.overrideCarryForward !== undefined) {
                    // Convert to strict boolean: 1 or true -> true, 0 or false -> false
                    const boolValue = enrollment.overrideCarryForward === 1 || enrollment.overrideCarryForward === true;
                    setOverrideCarryForward(boolValue);
                }

                // Load override charges if present (including legacy monthly/yearly fields)
                const legacyPeriodCharge = enrollment.overridePeriodCharge
                    ?? enrollment.overrideMonthlyCharge
                    ?? enrollment.overrideYearlyCharge;
                const legacyPeriodInterval = enrollment.overridePeriodInterval
                    ?? (enrollment.overrideChargesMode === "YEARLY" ? "YEARLY" : null);

                if (enrollment.overrideChargesMode || enrollment.overridePerSessionCharge != null
                    || legacyPeriodCharge != null || legacyPeriodInterval) {
                    setShowOverrideCharges(true);
                    setOverrideChargesMode(enrollment.overrideChargesMode || "");
                    setOverridePerSessionCharge(
                        enrollment.overridePerSessionCharge != null
                            ? String(enrollment.overridePerSessionCharge)
                            : ""
                    );
                    if (legacyPeriodCharge != null) {
                        setOverridePeriodCharge(String(legacyPeriodCharge));
                    }
                    if (legacyPeriodInterval) {
                        setOverridePeriodInterval(legacyPeriodInterval);
                    }
                }
            } catch (e) {
                console.error("Failed to load enrollment:", e);
                enrollmentLoadFailedRef.current = true;
                if (mounted) setError(t('enrollments.validation.loadEnrollmentFailed'));
            }
        })();

        return () => { mounted = false; };
    }, [isEditMode, enrollmentId, role]);

    // STEP 2: Load students and levels (once)
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const studentsRes =
                    role === "ADMIN"
                        ? await API.get("/admin/users", { params: { role: "STUDENT", page: 0, size: 500 } })
                        : role === "MANAGER"
                            ? await API.get("/manager/users", { params: { role: "STUDENT" } })
                            : await API.get("/coach/students", { params: { page: 0, size: 500 } });

                const rawStudents = studentsRes?.data?.content ?? studentsRes?.data ?? [];
                const normalized = rawStudents
                    .map(s => ({
                        ...s,
                        __id: s?.userId ?? s?.id ?? s?.user_id ?? null
                    }))
                    .filter(s => s.__id != null)
                    .filter(s => role !== "MANAGER" || s.active !== false)
                    .sort((a, b) => {
                        const aName = studentFullName(a).toLowerCase();
                        const bName = studentFullName(b).toLowerCase();
                        return aName.localeCompare(bName);
                    });

                const lvlRes = await listLevels(role, { page: 0, size: 500 });
                const levelsData = lvlRes?.data?.content ?? lvlRes?.data ?? [];

                if (!mounted) return;
                setStudents(normalized);
                setLevels(Array.isArray(levelsData) ? levelsData : []);
                if (!enrollmentLoadFailedRef.current) setError("");
            } catch (e) {
                console.error(e);
                if (mounted) setError(t('enrollments.validation.loadFailed'));
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [role]);

    // STEP 3: Load batches when levelId changes
    useEffect(() => {
        if (!levelId) {
            setBatches([]);
            if (!isEditMode) setBatchId(""); // Don't clear in edit mode
            return;
        }

        let mounted = true;
        (async () => {
            try {
                const res = await listBatches(role, { levelId: Number(levelId), page: 0, size: 500 });
                const list = res?.data?.content ?? res?.data ?? [];

                if (!mounted) return;
                setBatches(Array.isArray(list) ? list : []);
                setCascadeLoadError("");
            } catch (e) {
                console.error(e);
                if (mounted) {
                    setBatches([]);
                    setCascadeLoadError(t('common.optionsLoadFailed'));
                }
            }
        })();
        return () => { mounted = false; };
    }, [role, levelId, isEditMode, t]);

    // STEP 4: Load discounts when levelId AND batchId are available
    useEffect(() => {
        if (!levelId || !batchId) {
            setDiscounts([]);
            if (!isEditMode) setDiscountId(""); // Don't clear in edit mode
            return;
        }

        let mounted = true;
        (async () => {
            try {
                const res = await getApplicableDiscounts({ levelId, batchId }, role);

                if (!mounted) return;
                setDiscounts(res?.data ?? []);
                setCascadeLoadError("");
            } catch {
                if (mounted) {
                    setDiscounts([]);
                    setCascadeLoadError(t('common.optionsLoadFailed'));
                }
            }
        })();
        return () => { mounted = false; };
    }, [role, levelId, batchId, isEditMode, t]);

    // Handle URL params for pre-filling (create mode only)
    useEffect(() => {
        if (isEditMode) return;

        const lv = params.get("levelId");
        const bt = params.get("batchId");
        const sid = params.get("studentId");
        const renewFrom = params.get("renewFrom");
        if (lv) setLevelId(lv);
        if (bt) setBatchId(bt);
        if (sid) setSelectedStudentIds([String(sid)]);
        if (renewFrom) setRenewFromEnrollmentId(Number(renewFrom));
    }, [params, isEditMode]);

    // Renew flow: load prior enrollment and pre-fill dates
    useEffect(() => {
        if (isEditMode || !renewFromEnrollmentId) return;
        let mounted = true;
        (async () => {
            try {
                const res = await API.get(`${enrollmentApiBase(role)}/${renewFromEnrollmentId}`);
                const prior = res?.data;
                if (!mounted || !prior) return;
                setLevelId(String(prior.levelId || ""));
                setBatchId(String(prior.batchId || ""));
                if (prior.studentId) setSelectedStudentIds([String(prior.studentId)]);
                const start = computeRenewStartDate(prior.endEnrollmentDate);
                setEnrolledDateISO(start);
                setEnrolledDateUI(toRegionalDateFromISO(start));
                setEndDateTouched(false);
            } catch (e) {
                console.error("Failed to load prior enrollment for renew", e);
                if (mounted) setError(t('enrollments.validation.loadEnrollmentFailed'));
            }
        })();
        return () => { mounted = false; };
    }, [isEditMode, renewFromEnrollmentId, role, t]);

    // Auto end date for period batches when start or batch changes
    useEffect(() => {
        if (isEditMode || endDateTouched || !isPeriodBatch || !enrolledDateISO) return;
        const interval = selectedBatch?.periodInterval ?? selectedBatch?.period_interval ?? "MONTHLY";
        const end = computePeriodEndDate(enrolledDateISO, interval);
        if (end) {
            setEndEnrollmentDateISO(end);
            setEndEnrollmentDateUI(toRegionalDateFromISO(end));
        }
    }, [isEditMode, endDateTouched, isPeriodBatch, enrolledDateISO, batchId, selectedBatch]);

    // Close student dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (studentBoxRef.current && !studentBoxRef.current.contains(e.target)) {
                setStudentOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filteredStudents = useMemo(() => {
        const q = studentQuery.trim().toLowerCase();
        if (!q) return students;
        return students.filter(s =>
            studentFullName(s).toLowerCase().includes(q)
        );
    }, [students, studentQuery]);

    const askPaymentDecision = (preview, studentName) =>
        new Promise((resolve, reject) => {
            setPaymentModal({ preview, studentName, resolve, reject });
        });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        setEnrollmentResults(null);

        try {
            if (isEditMode) {
                const body = buildEnrollmentUpdateBody(
                    {
                        studentId,
                        levelId,
                        batchId,
                        enrolledDateISO,
                        endEnrollmentDateISO,
                        overrideCarryForward,
                        discountId,
                        overrideChargesMode,
                        overridePerSessionCharge,
                        overridePeriodCharge,
                        overridePeriodInterval,
                        showOverrideCharges,
                    },
                    editCtx
                );

                await paintBeforeAsync();
                await updateEnrollment(role, enrollmentId, body);
                navigate(
                    role === "MANAGER" ? "/manager/enrollments"
                        : role === "COACH" ? "/coach/enrollments"
                            : "/admin/enrollments"
                );
            } else {
                // Create mode - multiple students
                const studentsToEnroll = selectedStudentIds.length > 0 ? selectedStudentIds : [];

                if (studentsToEnroll.length === 0) {
                    setError(t('enrollments.validation.selectAtLeastOneStudent'));
                    setSubmitting(false);
                    return;
                }

                const selectedBatch = batches.find(b => (b.batchId ?? b.id) === Number(batchId));
                if (!isPeriodBatch && selectedBatch?.carryForwardMode === "ENROLLMENT_LEVEL") {
                    if (overrideCarryForward === null || overrideCarryForward === undefined) {
                        setError(t('enrollments.validation.carryForwardRequired'));
                        setSubmitting(false);
                        return;
                    }
                }

                if (isPeriodBatch && !endEnrollmentDateISO) {
                    setError(t('enrollments.validation.endDateRequired'));
                    setSubmitting(false);
                    return;
                }

                setEnrollmentProgress({
                    current: 0,
                    total: studentsToEnroll.length,
                    name: t("enrollments.form.preparing"),
                });
                await paintBeforeAsync();

                const results = { success: [], failed: [] };

                // Loop through each selected student
                for (let i = 0; i < studentsToEnroll.length; i++) {
                    const currentStudentId = studentsToEnroll[i];
                    const student = students.find(s => String(s.__id) === String(currentStudentId));
                    const studentName = studentFullName(student) || studentDisplayName(null, currentStudentId, t);

                    setEnrollmentProgress({ current: i + 1, total: studentsToEnroll.length, name: studentName });

                    const body = {
                        studentId: Number(currentStudentId),
                        levelId: Number(levelId),
                        batchId: Number(batchId),
                        enrolledDate: enrolledDateISO,
                        endEnrollmentDate: endEnrollmentDateISO || null,
                        discountId: discountId ? Number(discountId) : null,
                        renewedFromEnrollmentId: renewFromEnrollmentId || null,
                    };

                    if (isPeriodBatch) {
                        try {
                            const previewRes = await getEnrollmentPaymentPreview(role, {
                                studentId: currentStudentId,
                                batchId,
                                enrolledDate: enrolledDateISO,
                                endEnrollmentDate: endEnrollmentDateISO,
                            });
                            const preview = previewRes?.data ?? previewRes;
                            if (preview?.periodBilling) {
                                const decision = await askPaymentDecision(preview, studentName);
                                body.periodPaymentAction = decision.periodPaymentAction;
                                if (decision.confirmUnpaidPrior) {
                                    body.confirmUnpaidPrior = true;
                                }
                            }
                        } catch (modalErr) {
                            if (modalErr?.cancelled) {
                                setEnrollmentProgress(null);
                                setSubmitting(false);
                                return;
                            }
                            throw modalErr;
                        }
                    }

                    if (!isPeriodBatch && selectedBatch?.carryForwardMode === "ENROLLMENT_LEVEL") {
                        body.overrideCarryForward = overrideCarryForward;
                    } else if (!isPeriodBatch && overrideCarryForward !== null) {
                        body.overrideCarryForward = overrideCarryForward;
                    }

                    if (showOverrideCharges) {
                        body.overrideChargesMode = overrideChargesMode || null;
                        if (overrideChargesMode === "PER_PERIOD") {
                            body.overridePeriodCharge = overridePeriodCharge ? Number(overridePeriodCharge) : null;
                            body.overridePeriodInterval = overridePeriodInterval || null;
                        } else {
                            body.overridePerSessionCharge = overridePerSessionCharge ? Number(overridePerSessionCharge) : null;
                        }
                    }

                    try {
                        await enrollByStaff(role, body);
                        results.success.push({ studentId: currentStudentId, studentName });
                    } catch (err) {
                        const reason = apiErrorMessage(err, t('common.unknownError'));
                        results.failed.push({ studentId: currentStudentId, studentName, reason });
                    }
                }

                setEnrollmentProgress(null);
                setEnrollmentResults(results);

                // If all succeeded, give humans time to read the success summary before navigating.
                if (results.failed.length === 0) {
                    const listPath =
                        role === "MANAGER" ? "/manager/enrollments"
                            : role === "COACH" ? "/coach/enrollments"
                                : "/admin/enrollments";
                    setTimeout(() => navigate(listPath), 5000);
                }
            }
        } catch (err) {
            console.error(err);
            setError(apiErrorMessage(err, t('enrollments.validation.saveFailed')));
            setEnrollmentProgress(null);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={isEditMode ? t("enrollments.editEnrollment") : t("enrollments.newEnrollment")}
                subtitle={t("enrollments.formSubtitle")}
            />
        );
    }

    const selectedStudent = students.find(s => String(s.__id) === String(studentId));
    const selectedStudents = students.filter(s => selectedStudentIds.includes(String(s.__id)));

    const toggleStudentSelection = (sid) => {
        setSelectedStudentIds(prev =>
            prev.includes(String(sid))
                ? prev.filter(id => id !== String(sid))
                : [...prev, String(sid)]
        );
    };

    const selectAllFiltered = () => {
        const allFilteredIds = filteredStudents.map(s => String(s.__id));
        setSelectedStudentIds(allFilteredIds);
    };

    const clearSelection = () => {
        setSelectedStudentIds([]);
    };

    return (
        <div className="page-shell-form">
            {submitting && (
                <EnrollmentSavingOverlay progress={enrollmentProgress} isEditMode={isEditMode} />
            )}
            <PageHeaderSection
                title={isEditMode ? t("enrollments.editEnrollment") : t("enrollments.newEnrollment")}
                subtitle={t("enrollments.formSubtitle")}
            />

            {isEditMode && systemManaged && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 mb-4">
                    <LockedFieldNote className="!mt-0 !bg-transparent !border-0 !p-0">
                        {ENROLLMENT_LOCK_REASONS.systemManaged}
                    </LockedFieldNote>
                </div>
            )}

            {renewFromEnrollmentId && !isEditMode && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900 mb-4">
                    {t("enrollments.form.renewBanner", { id: renewFromEnrollmentId })}
                </div>
            )}

            <div className="form-shell">
                <form onSubmit={submit} className="space-y-4">
                    {cascadeLoadError && (
                        <div className="p-2 text-red-700 bg-red-100 rounded" role="status">{cascadeLoadError}</div>
                    )}
                    {error && <div className="p-2 text-red-700 bg-red-100 rounded">{error}</div>}

                    {/* Progress Indicator */}
                    {enrollmentProgress && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                <LoadingSpinner size="sm" inline className="!py-0" />
                                <div className="flex-1">
                                    <div className="font-semibold text-blue-900">
                                        {t("enrollments.form.enrolling", {
                                            current: enrollmentProgress.current,
                                            total: enrollmentProgress.total,
                                        })}
                                    </div>
                                    <div className="text-sm text-blue-700">{enrollmentProgress.name}</div>
                                </div>
                            </div>
                            <div className="mt-2 bg-blue-200 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-blue-600 h-full transition-all duration-300"
                                    style={{ width: `${(enrollmentProgress.current / enrollmentProgress.total) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Results Summary */}
                    {enrollmentResults && (
                        <div className="space-y-3">
                            {enrollmentResults.success.length > 0 && (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <div className="flex-1">
                                            <div className="font-semibold text-green-900">
                                                {t("enrollments.form.successCount", { count: enrollmentResults.success.length })}
                                            </div>
                                            <div className="text-sm text-green-700 mt-1">
                                                {enrollmentResults.success.map(s => s.studentName).join(', ')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {enrollmentResults.failed.length > 0 && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <div className="flex-1">
                                            <div className="font-semibold text-red-900">
                                                {t("enrollments.form.failedCount", { count: enrollmentResults.failed.length })}
                                            </div>
                                            <ul className="text-sm text-red-700 mt-2 space-y-1">
                                                {enrollmentResults.failed.map((f, idx) => (
                                                    <li key={idx}>
                                                        <span className="font-medium">{f.studentName}:</span> {f.reason}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {enrollmentResults.success.length > 0 && enrollmentResults.failed.length === 0 && (
                                <div className="text-center text-sm text-gray-600">
                                    {t("enrollments.form.redirecting")}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Student Selection */}
                    {isEditMode ? (
                        <div>
                            <label className="block text-sm mb-1 font-medium">{t("enrollments.form.student")}</label>
                            <input
                                type="text"
                                value={selectedStudent ? studentLabel(selectedStudent) : ""}
                                className="w-full border-2 p-2.5 rounded-lg bg-gray-100"
                                disabled
                            />
                        </div>
                    ) : (
                        <div ref={studentBoxRef}>
                            <TextInput
                                label={t("enrollments.form.selectStudents", { count: selectedStudentIds.length })}
                                type="text"
                                value={studentQuery}
                                onChange={(e) => {
                                    setStudentQuery(e.target.value);
                                    setStudentOpen(true);
                                }}
                                onFocus={() => setStudentOpen(true)}
                                placeholder={t("enrollments.form.searchStudents")}
                            />

                            {/* Selected Students Tags */}
                            {selectedStudents.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {selectedStudents.map(s => (
                                        <span
                                            key={s.__id}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                                        >
                                            {studentFullName(s) || `#${s.__id}`}
                                            <button
                                                type="button"
                                                onClick={() => toggleStudentSelection(s.__id)}
                                                className="hover:text-purple-900"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Dropdown with Checkboxes */}
                            {studentOpen && filteredStudents.length > 0 && (
                                <div className="absolute z-10 bg-white border-2 border-purple-300 rounded-lg mt-1 max-h-80 overflow-y-auto w-full shadow-lg">
                                    {/* Select All / Clear All */}
                                    <div className="sticky top-0 bg-gray-50 border-b px-3 py-2 flex justify-between">
                                        <button
                                            type="button"
                                            onClick={selectAllFiltered}
                                            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                                        >
                                            {t("enrollments.form.selectAll", { count: filteredStudents.length })}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={clearSelection}
                                            className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                                        >
                                            {t("enrollments.form.clear")}
                                        </button>
                                    </div>
                                    {filteredStudents.map((s) => {
                                        const isSelected = selectedStudentIds.includes(String(s.__id));
                                        return (
                                            <div
                                                key={s.__id}
                                                onClick={() => toggleStudentSelection(s.__id)}
                                                className={`px-3 py-2 cursor-pointer hover:bg-purple-50 flex items-center gap-2 ${isSelected ? 'bg-purple-100' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => { }}
                                                    className="w-4 h-4 text-purple-600"
                                                />
                                                <span className={isSelected ? 'font-medium text-purple-900' : ''}>
                                                    {studentLabel(s)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {studentOpen && filteredStudents.length === 0 && (
                                <div className="absolute z-10 mt-1 w-full rounded-lg border-2 border-slate-200 bg-white px-3 py-3 text-sm text-slate-600 shadow-lg">
                                    {students.length === 0
                                        ? t("enrollments.form.noStudentsAvailable", {
                                            defaultValue: "No students available to enroll.",
                                        })
                                        : t("enrollments.form.noStudentMatches", {
                                            defaultValue: "No students match your search.",
                                        })}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <SelectInput
                                label={t("enrollments.form.level")}
                                value={levelId}
                                onChange={(e) => setLevelId(e.target.value)}
                                required
                                disabled={isEditMode && !canEditEnrollmentField('level', editCtx)}
                                className={isEditMode ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''}
                            >
                                <option value="">{t("enrollments.form.select")}</option>
                                {levels.map((lv) => (
                                    <option key={lv.levelId} value={lv.levelId}>
                                        {formatLevelLabel(lv)}
                                    </option>
                                ))}
                            </SelectInput>
                            {isEditMode && !canEditEnrollmentField('level', editCtx) && (
                                <LockedFieldNote>{ENROLLMENT_LOCK_REASONS.level}</LockedFieldNote>
                            )}
                        </div>

                        <div>
                            <SelectInput
                                label={t("enrollments.form.trainingBatch")}
                                value={batchId}
                                onChange={(e) => setBatchId(e.target.value)}
                                required
                                disabled={!levelId || (isEditMode && !canEditEnrollmentField('batch', editCtx))}
                                className={isEditMode ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''}
                            >
                                <option value="">{t("enrollments.form.select")}</option>
                                {batches.map((b) => (
                                    <option key={b.batchId ?? b.id} value={b.batchId ?? b.id}>
                                        {b.name} (#{b.batchId ?? b.id})
                                    </option>
                                ))}
                            </SelectInput>
                            {isEditMode && !canEditEnrollmentField('batch', editCtx) && (
                                <LockedFieldNote>{ENROLLMENT_LOCK_REASONS.batch}</LockedFieldNote>
                            )}
                        </div>
                    </div>

                    {/* Enrollment Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-3">
                            <label className="flex text-sm mb-2 font-semibold text-gray-700 items-center gap-2">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {t("enrollments.form.startDate")}
                            </label>
                            <DatePicker
                                value={enrolledDateUI}
                                onChange={(val) => {
                                    setEnrolledDateUI(val);
                                    const iso = toISODateFromRegional(val);
                                    if (iso) setEnrolledDateISO(iso);
                                }}
                                disabled={isEditMode && !canEditEnrollmentField('startDate', editCtx)}
                                className={`w-full border-2 border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isEditMode ? 'opacity-60' : ''}`}
                            />
                            <div className="text-xs text-gray-500 mt-1.5">
                                {t("enrollments.form.startDateHint")}
                            </div>
                            {isEditMode && !canEditEnrollmentField('startDate', editCtx) && (
                                <LockedFieldNote>{ENROLLMENT_LOCK_REASONS.startDate}</LockedFieldNote>
                            )}
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-lg p-3">
                            <label className="flex text-sm mb-2 font-semibold text-gray-700 items-center gap-2">
                                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {t("enrollments.form.endDate")}{" "}
                                {isPeriodBatch && !isEditMode ? (
                                    <span className="text-red-600">*</span>
                                ) : (
                                    <span className="text-gray-400">{t("enrollments.form.optional")}</span>
                                )}
                            </label>
                            <DatePicker
                                value={endEnrollmentDateUI}
                                onChange={(val) => {
                                    setEndDateTouched(true);
                                    setEndEnrollmentDateUI(val);
                                    const iso = toISODateFromRegional(val);
                                    setEndEnrollmentDateISO(iso || "");
                                }}
                                clearable
                                placeholder={getDateFormatPlaceholder()}
                                disabled={isEditMode && !canEditEnrollmentField('endDate', editCtx)}
                                className={`w-full border-2 border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isEditMode && systemManaged ? 'opacity-60' : ''}`}
                            />
                            <div className="text-xs text-gray-500 mt-1.5">
                                {isPeriodBatch
                                    ? t("enrollments.form.endDatePeriodHint", {
                                        interval: formatPeriodInterval(selectedBatch?.periodInterval ?? selectedBatch?.period_interval) || t('common.period'),
                                    })
                                    : t("enrollments.form.endDateOpenHint")}
                            </div>
                            {isEditMode && canEditEnrollmentField('endDate', editCtx) && (
                                <p className="text-xs text-gray-600 mt-1.5">{ENROLLMENT_LOCK_REASONS.endDateEditable}</p>
                            )}
                        </div>
                    </div>

                    {/* Carry Forward Override — not applicable for period billing */}
                    {batchId && !isPeriodBatch && (() => {
                        const selectedBatch = batches.find(b => (b.batchId ?? b.id) === Number(batchId));
                        if (!selectedBatch) return null;

                        const batchCarryForwardMode = selectedBatch.carryForwardMode || "NOT_ALLOWED";
                        const isEnrollmentLevel = batchCarryForwardMode === "ENROLLMENT_LEVEL";

                        return (
                            <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                                <label className="block text-sm mb-2 font-semibold text-gray-700">
                                    {t("enrollments.form.carryForwardPolicy")}
                                </label>

                                {/* Batch Level Policy Display */}
                                <div className={`mb-3 p-3 rounded-lg border-2 ${batchCarryForwardMode === "ALLOWED"
                                        ? "bg-green-50 border-green-300"
                                        : batchCarryForwardMode === "NOT_ALLOWED"
                                            ? "bg-red-50 border-red-300"
                                            : "bg-blue-50 border-blue-300"
                                    }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-sm font-semibold">
                                            {t("enrollments.form.batchPolicy")} {batchPolicyLabel(batchCarryForwardMode)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-700 ml-6">
                                        {batchPolicySummary(batchCarryForwardMode)}
                                    </div>
                                </div>

                                {/* Enrollment Level Override (only if batch allows) */}
                                {isEnrollmentLevel ? (
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                            {t("enrollments.form.setCarryForward")} <span className="text-red-600">*</span>
                                        </div>
                                        <label className={`flex items-center gap-2 ${isEditMode ? 'opacity-60' : ''}`}>
                                            <input
                                                type="radio"
                                                name="carryForwardOverride"
                                                checked={overrideCarryForward === true}
                                                onChange={() => setOverrideCarryForward(true)}
                                                className="w-4 h-4"
                                                required={!isEditMode}
                                                disabled={isEditMode}
                                            />
                                            <span className="text-sm font-medium">{t("enrollments.form.enableCarryForward")}</span>
                                            <span className="text-xs text-gray-500">- {studentOverrideShort(true)}</span>
                                        </label>
                                        <label className={`flex items-center gap-2 ${isEditMode ? 'opacity-60' : ''}`}>
                                            <input
                                                type="radio"
                                                name="carryForwardOverride"
                                                checked={overrideCarryForward === false}
                                                onChange={() => setOverrideCarryForward(false)}
                                                className="w-4 h-4"
                                                required={!isEditMode}
                                                disabled={isEditMode}
                                            />
                                            <span className="text-sm font-medium">{t("enrollments.form.disableCarryForward")}</span>
                                            <span className="text-xs text-gray-500">- {studentOverrideShort(false)}</span>
                                        </label>
                                        {!isEditMode && (
                                            <div className="text-xs text-orange-600 mt-2 p-2 bg-orange-50 rounded">
                                                {t("enrollments.form.carryForwardRequired")}
                                            </div>
                                        )}
                                        {isEditMode && (
                                            <LockedFieldNote>{ENROLLMENT_LOCK_REASONS.carryForward}</LockedFieldNote>
                                        )}
                                    </div>
                                ) : (
                                    <div className="p-3 bg-gray-100 border border-gray-300 rounded text-sm text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-medium">{t("enrollments.form.carryForwardBatchLevel")}</span>
                                        </div>
                                        <div className="ml-6 mt-1 text-xs">
                                            {ENROLLMENT_LOCK_REASONS.carryForwardBatch}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Override Charges Section — create only, or edit existing custom rate */}
                    {( !isEditMode || hasCustomSessionRate ) && (
                    <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-700">
                                {t("enrollments.form.overrideBilling")}
                            </label>
                            {!isEditMode && (
                            <button
                                type="button"
                                onClick={() => setShowOverrideCharges(!showOverrideCharges)}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                {showOverrideCharges ? t("enrollments.form.hide") : t("enrollments.form.show")}
                            </button>
                            )}
                        </div>

                        {(showOverrideCharges || isEditMode) && (
                            <div className="space-y-3">
                                {!isEditMode && (
                                <SelectInput
                                    label={t("enrollments.form.billingMode")}
                                    value={overrideChargesMode}
                                    onChange={(e) => setOverrideChargesMode(e.target.value)}
                                >
                                    <option value="">{t("enrollments.form.select")}</option>
                                    {Object.entries(CHARGES_MODE_LABELS)
                                        .filter(([key]) => key === "PER_SESSION" || key === "PER_SESSION_HOURLY" || key === "PER_PERIOD")
                                        .map(([key]) => (
                                            <option key={key} value={key}>{formatChargesMode(key)}</option>
                                        ))}
                                </SelectInput>
                                )}

                                {isEditMode && (
                                    <LockedFieldNote>{ENROLLMENT_LOCK_REASONS.billingMode}</LockedFieldNote>
                                )}

                                {isEditMode && isPeriodBillingMode(overrideChargesMode) && (
                                    <div className="grid md:grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                                {t("enrollments.form.billingPeriod")}
                                            </div>
                                            <div className="font-medium text-gray-900">
                                                {formatPeriodInterval(
                                                    resolvePeriodInterval(
                                                        selectedBatch,
                                                        enrollmentChargeSnapshot({
                                                            overridePeriodInterval: overridePeriodInterval || null,
                                                            overridePeriodCharge: overridePeriodCharge
                                                                ? Number(overridePeriodCharge)
                                                                : null,
                                                        })
                                                    )
                                                ) || "—"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                                {t("enrollments.form.periodFee")}
                                            </div>
                                            <div className="font-medium text-gray-900">
                                                {(() => {
                                                    const amount = resolvePeriodCharge(
                                                        selectedBatch,
                                                        enrollmentChargeSnapshot({
                                                            overridePeriodInterval: overridePeriodInterval || null,
                                                            overridePeriodCharge: overridePeriodCharge
                                                                ? Number(overridePeriodCharge)
                                                                : null,
                                                            overrideMonthlyCharge: null,
                                                            overrideYearlyCharge: null,
                                                        })
                                                    );
                                                    return amount > 0 ? formatMoney(amount) : t("enrollments.form.notConfigured");
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {overrideChargesMode && (
                                    <div>
                                        {(overrideChargesMode === "PER_SESSION" || overrideChargesMode === "PER_SESSION_HOURLY") && (
                                            <>
                                            <TextInput
                                                label={overrideChargesMode === "PER_SESSION" ? t("enrollments.form.perSessionCharge") : t("enrollments.form.hourlyRate")}
                                                type="number"
                                                value={overridePerSessionCharge}
                                                onChange={(e) => setOverridePerSessionCharge(e.target.value)}
                                                placeholder="0.00"
                                                disabled={isEditMode && !canEditEnrollmentField('overridePerSessionCharge', editCtx)}
                                            />
                                            {isEditMode && canEditEnrollmentField('overridePerSessionCharge', editCtx) && (
                                                <p className="text-xs text-gray-600 mt-1">{ENROLLMENT_LOCK_REASONS.customRateEditable}</p>
                                            )}
                                            </>
                                        )}

                                        {!isEditMode && overrideChargesMode === "PER_PERIOD" && (
                                            <>
                                                <SelectInput
                                                    label={t("enrollments.form.billingPeriod")}
                                                    value={overridePeriodInterval}
                                                    onChange={(e) => setOverridePeriodInterval(e.target.value)}
                                                >
                                                    <option value="">{t("enrollments.form.useBatchDefault")}</option>
                                                    {getPeriodIntervalOptions().map(({ value, label }) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </SelectInput>
                                                <TextInput
                                                    label={t("enrollments.form.periodFeeAmount")}
                                                    type="number"
                                                    value={overridePeriodCharge}
                                                    onChange={(e) => setOverridePeriodCharge(e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    )}

                    {isEditMode && !hasCustomSessionRate && (
                        <LockedFieldNote>{ENROLLMENT_LOCK_REASONS.customRateLocked}</LockedFieldNote>
                    )}

                    {/* Pricing Summary Section */}
                    {batchId && (() => {
                        const selectedBatch = batches.find(b => (b.batchId ?? b.id) === Number(batchId));
                        const selectedDiscount = discounts.find(d => (d.id ?? d.discountId) === Number(discountId));

                        if (!selectedBatch) return null;

                        const batchChargesMode = selectedBatch.chargesMode || selectedBatch.charges_mode || "PER_SESSION_HOURLY";
                        const batchPerSession = selectedBatch.sessionCharge || 0;
                        const batchPeriodCharge = selectedBatch.periodCharge ?? selectedBatch.period_charge ?? 0;
                        const batchPeriodInterval = selectedBatch.periodInterval ?? selectedBatch.period_interval ?? "MONTHLY";

                        const discountType = selectedDiscount
                            ? (selectedDiscount.discountType || selectedDiscount.type || "PERCENT")
                            : null;
                        const discountRaw = selectedDiscount
                            ? Number(selectedDiscount.discountValue ?? selectedDiscount.value ?? 0)
                            : 0;
                        const applyDiscount = (amount) => {
                            if (!selectedDiscount || !discountRaw) return Number(amount || 0);
                            if (discountType === "FLAT") {
                                return Math.max(0, Number(amount || 0) - discountRaw);
                            }
                            return Number(amount || 0) * (1 - discountRaw / 100);
                        };

                        const effectiveChargesMode = overrideChargesMode || batchChargesMode;
                        const chargeSnapshot = enrollmentChargeSnapshot({
                            overridePeriodCharge: overridePeriodCharge ? Number(overridePeriodCharge) : null,
                            overridePeriodInterval: overridePeriodInterval || null,
                        });
                        const effectivePerSession = overridePerSessionCharge
                            ? Number(overridePerSessionCharge)
                            : batchPerSession;
                        const effectivePeriodCharge = resolvePeriodCharge(selectedBatch, chargeSnapshot);
                        const effectivePeriodInterval = resolvePeriodInterval(selectedBatch, chargeSnapshot);

                        const finalPerSession = formatCurrencyRaw(applyDiscount(effectivePerSession));
                        const finalPeriodCharge = formatCurrencyRaw(applyDiscount(effectivePeriodCharge));
                        const hasDiscount = !!selectedDiscount && discountRaw > 0;

                        const hasCustomOverride = overrideChargesMode || overridePerSessionCharge
                            || overridePeriodCharge || overridePeriodInterval;
                        const periodMode = isPeriodBillingMode(effectiveChargesMode);
                        const sessionMode = effectiveChargesMode === "PER_SESSION"
                            || effectiveChargesMode === "PER_SESSION_HOURLY";

                        return (
                            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border-2 border-purple-200">
                                <h3 className="font-bold text-lg text-purple-900 mb-3 flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t("enrollments.form.pricingSummary")}
                                </h3>

                                <div className="space-y-3">
                                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                            {t("enrollments.form.batchDefaultPricing")}
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-gray-700">{t("enrollments.form.chargesMode")}</span>
                                                <span className="font-semibold text-purple-900">{formatChargesMode(batchChargesMode)}</span>
                                            </div>
                                            {(batchChargesMode === "PER_SESSION" || batchChargesMode === "PER_SESSION_HOURLY") && batchPerSession > 0 && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-700">
                                                        {batchChargesMode === "PER_SESSION_HOURLY" ? t("enrollments.form.hourlyRate") : t("enrollments.form.perSession")}
                                                    </span>
                                                    <span className="font-semibold text-gray-900">{formatMoney(batchPerSession)}</span>
                                                </div>
                                            )}
                                            {isPeriodBillingMode(batchChargesMode) && (
                                                <>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-700">{t("enrollments.form.periodPlan")}</span>
                                                        <span className="font-semibold text-gray-900">
                                                            {formatPeriodInterval(batchPeriodInterval) || batchPeriodInterval}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-700">{t("enrollments.form.periodFee")}</span>
                                                        <span className="font-semibold text-gray-900">
                                                            {resolvePeriodCharge(selectedBatch, {}) > 0
                                                                ? formatMoney(resolvePeriodCharge(selectedBatch, {}))
                                                                : t("enrollments.form.notConfigured")}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Discount Selection */}
                                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                                        <SelectInput
                                            label={hasCustomOverride ? t("enrollments.form.applyDiscountCustom") : t("enrollments.form.applyDiscount")}
                                            value={discountId}
                                            onChange={(e) => setDiscountId(e.target.value)}
                                            disabled={isEditMode}
                                            className={isEditMode ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''}
                                        >
                                            <option value="">{t("enrollments.form.noDiscount")}</option>
                                            {discounts.map(d => (
                                                <option key={d.id ?? d.discountId} value={d.id ?? d.discountId}>
                                                    {d.name} - {t('enrollments.form.discountOff', {
                                                        value: formatDiscountValue(d.discountType || d.type, d.discountValue ?? d.value),
                                                        defaultValue: '{{value}} off',
                                                    })}
                                                </option>
                                            ))}
                                        </SelectInput>
                                        {isEditMode && (
                                            <LockedFieldNote>{ENROLLMENT_LOCK_REASONS.discount}</LockedFieldNote>
                                        )}
                                        {selectedDiscount && (
                                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                                                <div className="flex items-center gap-2 text-green-800">
                                                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="font-semibold">{selectedDiscount.name}</span>
                                                </div>
                                                <div className="ml-6 text-green-700">
                                                    {t("enrollments.form.discountApplied", { value: selectedDiscount.discountValue ?? selectedDiscount.value })}
                                                    {(hasCustomOverride) && (
                                                        <span className="font-semibold">{t("enrollments.form.discountAppliedCustom")}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Override Indicator */}
                                    {(hasCustomOverride) && (
                                        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                                            <div className="flex items-start gap-2">
                                                <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <div className="flex-1">
                                                    <div className="font-semibold text-amber-900 text-sm">{t("enrollments.form.customChargesApplied")}</div>
                                                    <div className="text-xs text-amber-700 mt-1">
                                                        {t("enrollments.form.customChargesDesc")}
                                                        {selectedDiscount && (
                                                            <span className="block mt-1 font-medium">
                                                                {t("enrollments.form.customChargesDiscount", {
                                                                    value: selectedDiscount.discountValue ?? selectedDiscount.value,
                                                                    name: selectedDiscount.name,
                                                                })}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Final Charges */}
                                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-4 text-white">
                                        <div className="text-xs font-semibold uppercase tracking-wide mb-2 opacity-90">
                                            {t("enrollments.form.finalCharges")}
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm">{t("enrollments.form.pricingModel")}</span>
                                                <span className="font-bold text-lg">{formatChargesMode(effectiveChargesMode)}</span>
                                            </div>
                                            {sessionMode && effectivePerSession > 0 && (
                                                <div className="border-t border-white/30 pt-2">
                                                    <div className="flex justify-between items-baseline">
                                                        <span className="text-sm opacity-90">
                                                            {effectiveChargesMode === "PER_SESSION_HOURLY" ? t("enrollments.form.perHour") : t("enrollments.form.perSession")}
                                                        </span>
                                                        <div className="text-right">
                                                            {hasDiscount && (
                                                                <div className="text-xs line-through opacity-75">{formatMoney(effectivePerSession)}</div>
                                                            )}
                                                            <div className="font-bold text-2xl">{formatMoney(finalPerSession)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {periodMode && (
                                                <div className="border-t border-white/30 pt-2 space-y-2">
                                                    <div className="flex justify-between items-baseline">
                                                        <span className="text-sm opacity-90">{t("enrollments.form.periodPlan")}</span>
                                                        <span className="font-semibold">
                                                            {formatPeriodInterval(effectivePeriodInterval) || effectivePeriodInterval || "—"}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-baseline">
                                                        <span className="text-sm opacity-90">{t("enrollments.form.periodFee")}</span>
                                                        <div className="text-right">
                                                            {effectivePeriodCharge > 0 ? (
                                                                <>
                                                                    {hasDiscount && (
                                                                        <div className="text-xs line-through opacity-75">{formatMoney(effectivePeriodCharge)}</div>
                                                                    )}
                                                                    <div className="font-bold text-2xl">{formatMoney(finalPeriodCharge)}</div>
                                                                </>
                                                            ) : (
                                                                <div className="font-bold text-lg opacity-90">{t("enrollments.form.notConfigured")}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => navigate(
                                role === "MANAGER" ? "/manager/enrollments"
                                    : role === "COACH" ? "/coach/enrollments"
                                        : "/admin/enrollments"
                            )}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                            disabled={submitting}
                        >
                            {t("forms.common.cancel")}
                        </button>
                        <Button
                            type="submit"
                            loading={submitting}
                            disabled={submitting || (isEditMode && systemManaged)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {isEditMode ? t("enrollments.form.updateEnrollment") : t("enrollments.form.createEnrollment")}
                        </Button>
                    </div>
                </form>
            </div>

            <EnrollmentPaymentModal
                open={!!paymentModal}
                preview={paymentModal?.preview}
                studentName={paymentModal?.studentName}
                role={role}
                onRefreshPreview={
                    paymentModal?.preview?.studentId
                        ? async () => {
                            const previewRes = await getEnrollmentPaymentPreview(role, {
                                studentId: paymentModal.preview.studentId,
                                batchId,
                                enrolledDate: enrolledDateISO,
                                endEnrollmentDate: endEnrollmentDateISO,
                            });
                            const preview = previewRes?.data ?? previewRes;
                            setPaymentModal((prev) => (prev ? { ...prev, preview } : prev));
                        }
                        : undefined
                }
                onConfirm={(decision) => {
                    paymentModal?.resolve(decision);
                    setPaymentModal(null);
                }}
                onCancel={() => {
                    paymentModal?.reject({ cancelled: true });
                    setPaymentModal(null);
                }}
            />
        </div>
    );
};

export default EnrollmentForm;
